import type { BrandStyleKitDraft } from './BrandStyleKitDraft';

export const DESIGN_SYSTEM_SCHEMA = 'segmently-design-system/v1';

const TEXT_POLICY_MODES = ['render_in_model', 'mixed'] as const;
const ILLUSTRATION_STYLES = ['photographic', 'flat_vector', '3d_clay', 'hand_drawn', 'brutalist'] as const;

type TextPolicyMode = typeof TEXT_POLICY_MODES[number];
const DEFAULT_TEXT_POLICY_MODE: TextPolicyMode = 'render_in_model';

export interface DesignSystemExport extends Record<string, unknown> {
  _schema: typeof DESIGN_SYSTEM_SCHEMA;
  _about: string;
  _generated_at: string;

  design_description: string;
  _design_description_help: string;

  _palette_help: string;
  palette_primary: string;
  _palette_primary_help: string;
  palette_accent: string;
  _palette_accent_help: string;
  palette_neutral: string[];
  _palette_neutral_help: string;
  palette_forbidden: string[];
  _palette_forbidden_help: string;

  _typography_help: string;
  typography_primary_font: string;
  _typography_primary_font_help: string;
  typography_heading_style: string;
  _typography_heading_style_help: string;
  typography_body_style: string;
  _typography_body_style_help: string;

  _mood_keywords_help: string;
  mood_keywords: string[];

  _illustration_help: string;
  illustration_style: string;
  _illustration_style_help_enum: string;
  illustration_line_weight: string;
  _illustration_line_weight_help: string;
  illustration_shadow: string;
  _illustration_shadow_help: string;
  illustration_texture: string;
  _illustration_texture_help: string;

  _photo_help: string;
  photo_camera: string;
  _photo_camera_help: string;
  photo_lighting: string;
  _photo_lighting_help: string;
  photo_background: string;
  _photo_background_help: string;

  _composition_help: string;
  composition_grid: string;
  _composition_grid_help: string;
  composition_spacing: string;
  _composition_spacing_help: string;
  composition_hierarchy: string;
  _composition_hierarchy_help: string;

  _motifs_help: string;
  brand_motifs: string[];

  _forbidden_visuals_help: string;
  forbidden_visuals: string[];

  _text_policy_help: string;
  text_policy_mode: TextPolicyMode;
  _text_policy_mode_enum: string;
  text_policy_exact_text_required: boolean;
  _text_policy_exact_text_required_help: string;
}

export type ImportedDesignSystemPatch = Partial<Pick<BrandStyleKitDraft,
  | 'designDescription'
  | 'primaryColor'
  | 'accentColor'
  | 'neutralColors'
  | 'forbiddenColors'
  | 'primaryFontName'
  | 'headingStyle'
  | 'bodyStyle'
  | 'moodKeywords'
  | 'imageTreatment'
  | 'illustrationLineWeight'
  | 'illustrationShadow'
  | 'illustrationTexture'
  | 'photoCamera'
  | 'photoLighting'
  | 'photoBackground'
  | 'compositionGrid'
  | 'compositionSpacing'
  | 'compositionHierarchy'
  | 'brandMotifs'
  | 'forbiddenVisuals'
  | 'textPolicyMode'
  | 'exactTextRequired'
>>;

export type ImportDesignSystemResult =
  | { ok: true; draft: ImportedDesignSystemPatch; warnings: string[] }
  | { ok: false; error: string };

const HELP_TEXT = {
  about:
    'Prompt-relevant fields only. Each value field has a sibling `_<key>_help` describing what it means and how it reaches the image-generation prompt. Unknown keys are ignored on import.',

  design_description:
    'Freeform overall design brief for this scope (e.g. "Single image landscape 1.91:1 profile for LinkedIn… use shared navy/orange/neutral palette and clean modular grid"). The resolver concatenates this across all design layers and exposes it to image agents as `BrandStyleKit.visualPositioning` and as the `visual_positioning:` key inside the YAML `stylePromptBlock`. When palette / typography / composition / illustration / photo / motifs are all sparse, the composer falls back to the full YAML block — so this freeform sentence reaches the image prompt verbatim. Even when not in fallback, it is the source of truth for the overall direction.',

  palette:
    'Brand color palette. Primary + accent + neutral hex values are merged into one prompt sentence: `Use palette #FF6B35, #1A1A1A, #F5F5F5.` Forbidden hex values are merged into the Avoid sentence.',
  palette_primary:
    'Dominant brand hex color (e.g. `#112233`). Becomes the first hex in the `Use palette` prompt sentence.',
  palette_accent:
    'Accent / CTA hex color (e.g. `#FF6600`). Becomes the second hex in the `Use palette` prompt sentence.',
  palette_neutral:
    'Neutral background / text hex array (1-4 colors). Mapped to BrandStyleKit.palette.secondary and joined into the same `Use palette` sentence.',
  palette_forbidden:
    'Forbidden hex array (e.g. competitor red). Merged with forbidden_visuals into one `Avoid:` sentence.',

  typography:
    'Typography becomes one sentence: `Typography feel: <primary_font / heading_style / body_style>; <heading_style>; <body_style>.` The three sub-fields are joined with ` / ` to form the fallback description, then heading and body are re-listed.',
  typography_primary_font:
    'Font family name or description (e.g. `Inter / editorial grotesk`). The model cannot reproduce a specific font but uses this as a verbal cue for the typography feel.',
  typography_heading_style:
    'Free-text description of how headlines look (e.g. `large bold readable sans; 5-9 words max; high contrast`). Becomes part of the `Typography feel:` sentence.',
  typography_body_style:
    'Free-text description of body / supporting text style. Same prompt treatment as heading style.',

  mood_keywords:
    '3-7 short adjectives describing overall feel (e.g. `minimal`, `confident`, `editorial`). Not a standalone sentence — they fall back into `illustration_texture` and `photo_lighting` when those are empty, and are also spread into `brand_motifs`.',

  illustration:
    'Non-photographic visual treatment. Fill when illustration_style is anything other than `photographic`. Four sub-fields become one sentence: `Visual treatment: <style>; <line_weight>; <shadow>; <texture>.`',
  illustration_style_enum:
    'Allowed values: photographic | flat_vector | 3d_clay | hand_drawn | brutalist. Use `photographic` only when the post is a real photo — in that case fill photo_* fields and leave illustration_* detail fields blank.',
  illustration_line_weight:
    'Stroke / line weight description (e.g. `2px monoline, uniform`). Part of the `Visual treatment:` sentence.',
  illustration_shadow:
    'Shadow / depth treatment (e.g. `no shadow, flat` or `soft drop, 8px blur`). Part of the `Visual treatment:` sentence.',
  illustration_texture:
    'Texture / surface treatment (e.g. `clean vector, no texture`). Part of the `Visual treatment:` sentence. If empty, mood_keywords are used as a fallback.',

  photo:
    'Photographic treatment. Fill only when illustration_style = `photographic`; otherwise leave blank. Three sub-fields become one sentence: `Photo direction: <camera>; <lighting>; <background>.`',
  photo_camera:
    'Camera / lens / crop / framing rule (e.g. `35mm prime, eye-level, half-body crop`). Part of the `Photo direction:` sentence.',
  photo_lighting:
    'Lighting mood and contrast (e.g. `soft window key, warm 5500K, low contrast`). Part of the `Photo direction:` sentence. If empty, mood_keywords are used as a fallback.',
  photo_background:
    'Background / scene context (e.g. `minimal beige seamless backdrop, no props`). Part of the `Photo direction:` sentence.',

  composition:
    'Layout rhythm. Three sub-fields become one sentence: `Composition: <grid>; <spacing>; <hierarchy>.`',
  composition_grid:
    'Grid or framing rule (e.g. `asymmetric 2-zone split` or `centered single column`).',
  composition_spacing:
    'Margin, density, whitespace rule (e.g. `generous whitespace, 64px margin rhythm`).',
  composition_hierarchy:
    'Visual priority order (e.g. `headline top-left → hero centre → CTA bottom-right`).',

  motifs:
    'Repeated visual devices that carry consistency across a series (e.g. `circular badge with star`, `thick underline accent`). Become the sentence `Repeat motifs: <a>, <b>, <c>.`',

  forbidden_visuals:
    'Forbidden visual patterns (e.g. `no stock-photo cliches`, `no glassmorphism`, `no gradients on text`). Merged with palette_forbidden into one `Avoid:` sentence.',

  text_policy:
    'Controls how readable text appears in the generated image. `render_in_model` tells the image model to render the supplied short strings directly in the generated image; `mixed` allows a single main headline while body remains placeholder bars. `overlay_after_generation` is deprecated and is normalized to `render_in_model` on import.',
  text_policy_mode_enum:
    'Allowed values: render_in_model | mixed. Deprecated overlay_after_generation imports are converted to render_in_model.',
  text_policy_exact_text_required:
    'When true, the image must render the exact strings supplied by the storyboard.',
} as const;

function splitLines(value: unknown): string[] {
  if (typeof value !== 'string') return [];
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function joinLines(values: unknown): string {
  if (!Array.isArray(values)) return '';
  return values
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .join('\n');
}

function normalizeHex(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9A-Fa-f]{3,8}$/.test(withHash) ? withHash.toLowerCase() : '';
}

function normalizeHexArray(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return values.map(normalizeHex).filter((v) => v.length > 0);
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

function asTextPolicyMode(value: unknown, warnings: string[]): TextPolicyMode {
  if (typeof value === 'string' && (TEXT_POLICY_MODES as readonly string[]).includes(value)) {
    return value as TextPolicyMode;
  }
  if (value === 'overlay_after_generation') {
    warnings.push('text_policy_mode "overlay_after_generation" is deprecated in the UI; importing as "render_in_model".');
    return DEFAULT_TEXT_POLICY_MODE;
  }
  if (value !== undefined && value !== null && value !== '') {
    warnings.push(`text_policy_mode value "${String(value)}" is not allowed; defaulting to "render_in_model".`);
  }
  return DEFAULT_TEXT_POLICY_MODE;
}

function asIllustrationStyle(value: unknown, warnings: string[]): string {
  const trimmed = asString(value);
  if (!trimmed) return '';
  if ((ILLUSTRATION_STYLES as readonly string[]).includes(trimmed)) return trimmed;
  warnings.push(`illustration_style value "${trimmed}" is not in the allowed enum; passing through as-is.`);
  return trimmed;
}

export function exportDesignSystem(draft: BrandStyleKitDraft): DesignSystemExport {
  const textPolicyMode: TextPolicyMode = (TEXT_POLICY_MODES as readonly string[]).includes(draft.textPolicyMode)
    ? (draft.textPolicyMode as TextPolicyMode)
    : DEFAULT_TEXT_POLICY_MODE;

  return {
    _schema: DESIGN_SYSTEM_SCHEMA,
    _about: HELP_TEXT.about,
    _generated_at: new Date().toISOString(),

    design_description: draft.designDescription.trim(),
    _design_description_help: HELP_TEXT.design_description,

    _palette_help: HELP_TEXT.palette,
    palette_primary: normalizeHex(draft.primaryColor),
    _palette_primary_help: HELP_TEXT.palette_primary,
    palette_accent: normalizeHex(draft.accentColor),
    _palette_accent_help: HELP_TEXT.palette_accent,
    palette_neutral: splitLines(draft.neutralColors).map(normalizeHex).filter((v) => v.length > 0),
    _palette_neutral_help: HELP_TEXT.palette_neutral,
    palette_forbidden: splitLines(draft.forbiddenColors).map(normalizeHex).filter((v) => v.length > 0),
    _palette_forbidden_help: HELP_TEXT.palette_forbidden,

    _typography_help: HELP_TEXT.typography,
    typography_primary_font: draft.primaryFontName.trim(),
    _typography_primary_font_help: HELP_TEXT.typography_primary_font,
    typography_heading_style: draft.headingStyle.trim(),
    _typography_heading_style_help: HELP_TEXT.typography_heading_style,
    typography_body_style: draft.bodyStyle.trim(),
    _typography_body_style_help: HELP_TEXT.typography_body_style,

    _mood_keywords_help: HELP_TEXT.mood_keywords,
    mood_keywords: splitLines(draft.moodKeywords),

    _illustration_help: HELP_TEXT.illustration,
    illustration_style: draft.imageTreatment.trim(),
    _illustration_style_help_enum: HELP_TEXT.illustration_style_enum,
    illustration_line_weight: draft.illustrationLineWeight.trim(),
    _illustration_line_weight_help: HELP_TEXT.illustration_line_weight,
    illustration_shadow: draft.illustrationShadow.trim(),
    _illustration_shadow_help: HELP_TEXT.illustration_shadow,
    illustration_texture: draft.illustrationTexture.trim(),
    _illustration_texture_help: HELP_TEXT.illustration_texture,

    _photo_help: HELP_TEXT.photo,
    photo_camera: draft.photoCamera.trim(),
    _photo_camera_help: HELP_TEXT.photo_camera,
    photo_lighting: draft.photoLighting.trim(),
    _photo_lighting_help: HELP_TEXT.photo_lighting,
    photo_background: draft.photoBackground.trim(),
    _photo_background_help: HELP_TEXT.photo_background,

    _composition_help: HELP_TEXT.composition,
    composition_grid: draft.compositionGrid.trim(),
    _composition_grid_help: HELP_TEXT.composition_grid,
    composition_spacing: draft.compositionSpacing.trim(),
    _composition_spacing_help: HELP_TEXT.composition_spacing,
    composition_hierarchy: draft.compositionHierarchy.trim(),
    _composition_hierarchy_help: HELP_TEXT.composition_hierarchy,

    _motifs_help: HELP_TEXT.motifs,
    brand_motifs: splitLines(draft.brandMotifs),

    _forbidden_visuals_help: HELP_TEXT.forbidden_visuals,
    forbidden_visuals: splitLines(draft.forbiddenVisuals),

    _text_policy_help: HELP_TEXT.text_policy,
    text_policy_mode: textPolicyMode,
    _text_policy_mode_enum: HELP_TEXT.text_policy_mode_enum,
    text_policy_exact_text_required: draft.exactTextRequired === true,
    _text_policy_exact_text_required_help: HELP_TEXT.text_policy_exact_text_required,
  };
}

export function importDesignSystem(input: unknown): ImportDesignSystemResult {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, error: 'Input must be a JSON object.' };
  }
  const source = input as Record<string, unknown>;
  const warnings: string[] = [];

  const schema = source._schema;
  if (typeof schema === 'string' && schema !== DESIGN_SYSTEM_SCHEMA) {
    warnings.push(`Unknown _schema "${schema}" (expected "${DESIGN_SYSTEM_SCHEMA}"). Importing anyway.`);
  } else if (schema === undefined) {
    warnings.push(`Missing _schema field. Importing as "${DESIGN_SYSTEM_SCHEMA}".`);
  }

  const draft: ImportedDesignSystemPatch = {
    designDescription: asString(source.design_description),

    primaryColor: normalizeHex(source.palette_primary),
    accentColor: normalizeHex(source.palette_accent),
    neutralColors: joinLines(normalizeHexArray(source.palette_neutral)),
    forbiddenColors: joinLines(normalizeHexArray(source.palette_forbidden)),

    primaryFontName: asString(source.typography_primary_font),
    headingStyle: asString(source.typography_heading_style),
    bodyStyle: asString(source.typography_body_style),

    moodKeywords: joinLines(asStringArray(source.mood_keywords)),

    imageTreatment: asIllustrationStyle(source.illustration_style, warnings),
    illustrationLineWeight: asString(source.illustration_line_weight),
    illustrationShadow: asString(source.illustration_shadow),
    illustrationTexture: asString(source.illustration_texture),

    photoCamera: asString(source.photo_camera),
    photoLighting: asString(source.photo_lighting),
    photoBackground: asString(source.photo_background),

    compositionGrid: asString(source.composition_grid),
    compositionSpacing: asString(source.composition_spacing),
    compositionHierarchy: asString(source.composition_hierarchy),

    brandMotifs: joinLines(asStringArray(source.brand_motifs)),
    forbiddenVisuals: joinLines(asStringArray(source.forbidden_visuals)),

    textPolicyMode: asTextPolicyMode(source.text_policy_mode, warnings),
    exactTextRequired: source.text_policy_exact_text_required === true,
  };

  return { ok: true, draft, warnings };
}

export function buildExportFileName(platformId: string, profileKey: string): string {
  const today = new Date().toISOString().slice(0, 10);
  const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, '_') || 'unknown';
  return `design-system--${sanitize(platformId)}--${sanitize(profileKey)}--${today}.json`;
}
